import {
  ApplicationBarcodeScanner,
  BrowserClock,
  BrowserIdGenerator,
  BrowserFoodRecognizer,
  BrowserStoragePersistence,
  CrossTabCoordinator,
  DietTrackerDatabase,
  GoogleDriveApplicationBackupStore,
  GoogleDriveAuthorization,
  GoogleDriveBackupStore,
  IndexedDbLocalBackupGateway,
  LocalBackupManager,
  OpenFoodFactsFoodSource,
  PwaUpdateManager,
  createDomainRepositories,
} from "../infrastructure";
import { IndexedDbConfigurationRepository } from "../infrastructure/persistence/indexed-db/repositories/configuration-repository";
import { BrowserAppController } from "./browser-app-controller";

const APP_VERSION = "0.1.0";

export async function bootstrapApplication(): Promise<BrowserAppController> {
  const database = new DietTrackerDatabase();
  await database.open();
  const storedProfile = await database.profiles.get("primary");
  const clock = new BrowserClock(storedProfile?.homeTimeZone);
  const coordinator = new CrossTabCoordinator();
  const repositories = createDomainRepositories(database);
  const configurationRecords = new IndexedDbConfigurationRepository(database);
  const localBackupManager = new LocalBackupManager(database, APP_VERSION, coordinator);
  const localBackup = new IndexedDbLocalBackupGateway(localBackupManager);
  const clientId = import.meta.env.VITE_GOOGLE_CLIENT_ID?.trim();
  const authorization = clientId ? new GoogleDriveAuthorization(clientId) : undefined;
  const remoteBackup = authorization
    ? new GoogleDriveApplicationBackupStore(
        new GoogleDriveBackupStore(authorization),
        localBackupManager,
      )
    : undefined;

  const controller = new BrowserAppController({
    database,
    repositories,
    configurationRecords,
    clock,
    ids: browserIds,
    foodSources: [new OpenFoodFactsFoodSource()],
    barcodeScanner: new ApplicationBarcodeScanner(),
    foodRecognizer: new BrowserFoodRecognizer(),
    localBackup,
    remoteBackup,
    authorization,
    storagePersistence: new BrowserStoragePersistence(),
    coordinator,
  });
  await controller.initialize();

  if (import.meta.env.PROD) registerPwaUpdates();
  return controller;
}

const browserIds = new BrowserIdGenerator();

function registerPwaUpdates(): void {
  const manager = new PwaUpdateManager();
  manager.subscribe((state) => {
    if (state.status !== "update-available") return;
    if (window.confirm("An updated version of Diet Tracker is ready. Reload now?")) {
      void manager.activateUpdate();
    }
  });
  void manager.register();
}
